import { Injectable } from '@angular/core';

import { CashoutErrorType } from '@bpos/v1/cashout';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { MoneyService } from '@frontend/sports/common/core/feature/money';
import { StringInterpolationPipe } from '@frontend/sports/common/core/utils/string-interpolation';
import { PlacementErrorType } from '@frontend/sports/types/betslip';
import { StakeErrorType, StakeValidationResult } from 'packages/sports/web/app/src/betslip-base/stake-validation/stake-validation.models';
import { Observable, map, of } from 'rxjs';

import { BetslipError } from '../../validation/errors/betslip-error';
import { ClientErrorType } from '../../validation/errors/client-error-type';
import { EditBetAddedStakeNotDividablePreCheckError } from '../../validation/errors/pre-check/edit-bet-added-stake-not-dividable-pre-check-error';
import { StakeNotDividablePreCheckError } from '../../validation/errors/pre-check/stake-not-dividable-pre-check-error';
import { BetslipErrorMessageFormatterService } from '../../validation/services/formatting/betslip-error-message-formatter.service';
import { isNotEnoughMoney } from '../../validation/services/utils/betslip-errors-utils';

@Injectable()
export class EditBetErrorService {
    constructor(
        private errorMessageFormatter: BetslipErrorMessageFormatterService,
        private sitecore: Sitecore,
        private interpolatePipe: StringInterpolationPipe,
        private moneyService: MoneyService,
    ) {}

    private readonly keypadErrorTypes: (PlacementErrorType | CashoutErrorType | ClientErrorType)[] = [
        PlacementErrorType.InsufficientMoney,
        PlacementErrorType.NotWholeStake,
    ];

    getError(errors: BetslipError[]): BetslipError[] {
        return this.getToastErrors(errors);
    }

    getKeypadError(errors: BetslipError[]): Observable<StakeValidationResult | undefined> {
        return this.buildKeypadError(errors);
    }

    private getToastErrors(errors: BetslipError[]): BetslipError[] {
        return errors.filter((error) => this.keypadErrorTypes.indexOf(error.type) === -1);
    }

    private buildKeypadError(errors: BetslipError[]): Observable<StakeValidationResult | undefined> {
        const notEnoughMoneyError = errors.find(isNotEnoughMoney);
        if (notEnoughMoneyError) {
            const obj = {
                amountWithCurrency: this.moneyService.transform(
                    Math.abs(notEnoughMoneyError.userMoney - (notEnoughMoneyError.stake || 0)).toFixed(2),
                    true,
                ),
            };

            return of({
                message: this.interpolatePipe.transform(this.sitecore.betslip.validation.NotEnoughMoney, obj),
                type: StakeErrorType.MoreThanBalance,
            });
        }

        const stakeNotDividableError = errors.find((error) => error instanceof EditBetAddedStakeNotDividablePreCheckError);

        if (stakeNotDividableError) {
            return this.errorMessageFormatter.getErrorMessage(stakeNotDividableError).pipe(
                map((errorMessage) => ({
                    message: `${errorMessage}`,
                    type: StakeErrorType.NotMultiple,
                })),
            );
        }

        const totalstakeNotDividableError = errors.find((error) => error instanceof StakeNotDividablePreCheckError);

        if (totalstakeNotDividableError) {
            return this.errorMessageFormatter.getErrorMessage(totalstakeNotDividableError).pipe(
                map((errorMessage) => ({
                    message: `${errorMessage}`,
                    type: StakeErrorType.NotMultiple,
                })),
            );
        }

        return of(void 0);
    }
}
